import ActionDialog from "@dashboard/components/ActionDialog/ActionDialog";
import {
  type AtticApiKeyFragment,
  type AtticErrorFragment,
  useAtticApiKeyDetailsQuery,
  useAtticApiKeyRevokeMutation,
  useAtticApiKeySecretCreateMutation,
  useAtticApiKeySecretDeleteMutation,
  useAtticApiKeyUpdateMutation,
} from "@dashboard/graphql";
import { useNotifier } from "@dashboard/hooks/useNotifier/useNotifier";
import { getMutationState } from "@dashboard/misc";
import { useMemo, useState } from "react";
import { useIntl } from "react-intl";

import AtticApiKeyDetailsPage, {
  AtticApiKeyLoading,
  AtticApiKeyMissing,
} from "../components/AtticApiKeyDetailsPage/AtticApiKeyDetailsPage";
import AtticApiKeySecretDialog from "../components/AtticApiKeySecretDialog/AtticApiKeySecretDialog";
import { getAtticApiKeyErrorMessage, getExpiryDayError } from "../errors";
import { detailMessages, sectionMessages } from "../messages";
import {
  apiKeyToFormData,
  exceedsViewerPermissions,
  getUpdateInput,
  getUsageRows,
  getUsageTotals,
  getUsageWindow,
  isApiKeyGlobalId,
  todayLocalDay,
} from "../utils";
import { usePermissionChoices } from "./usePermissionChoices";

type Secret = AtticApiKeyFragment["secrets"][number];

export const AtticApiKeyDetailsView = ({ id }: { id: string }) => {
  const intl = useIntl();
  const notify = useNotifier();
  const { choices, userPermissions } = usePermissionChoices();
  // The window is fixed when the page opens, so the query does not change
  // under a page left open past midnight UTC.
  const [now] = useState(() => Date.now());
  const usageWindow = useMemo(() => getUsageWindow(now), [now]);
  const [errors, setErrors] = useState<AtticErrorFragment[]>([]);
  const [newSecret, setNewSecret] = useState<string | null>(null);
  // The secret hook keeps no result (ignoreResults), so its busy state is here.
  const [addingSecret, setAddingSecret] = useState(false);
  const [secretToDelete, setSecretToDelete] = useState<Secret | null>(null);
  const [revokeOpen, setRevokeOpen] = useState(false);
  // An id that is not a key's is no key at all, and reloading would not
  // change that: it is not asked about.
  const wellFormed = isApiKeyGlobalId(id);
  const { data, loading, error, refetch } = useAtticApiKeyDetailsQuery({
    variables: { id, since: usageWindow.since, until: usageWindow.until },
    skip: !wellFormed,
  });
  const apiKey = data?.atticApiKey;
  // A refusal of a secret or revoke action is a toast: it has no field.
  const notifyRefusal = (refusal: AtticErrorFragment) =>
    notify({ status: "error", text: getAtticApiKeyErrorMessage(refusal, intl) });

  const [updateKey, updateOpts] = useAtticApiKeyUpdateMutation({
    disableNestedErrorHandling: true,
    onCompleted: result => {
      const payload = result.atticApiKeyUpdate;

      setErrors(payload?.errors ?? []);

      if (payload && !payload.errors.length) {
        notify({ status: "success", text: intl.formatMessage(sectionMessages.saved) });
      }
    },
  });
  const [createSecret] = useAtticApiKeySecretCreateMutation({
    // The payload carries the one-time secret, so it is kept out of the Apollo
    // cache (no-cache) and out of the hook's own result (ignoreResults: this
    // Apollo, 3.4, has no reset()); onCompleted still receives it, and it lives
    // in this view's state only until the dialog closes. The key is re-read
    // instead, which brings the new secret's row (last four only).
    fetchPolicy: "no-cache",
    ignoreResults: true,
    disableNestedErrorHandling: true,
    onError: () => setAddingSecret(false),
    onCompleted: result => {
      setAddingSecret(false);

      const payload = result.atticApiKeySecretCreate;
      const [refusal] = payload?.errors ?? [];

      if (refusal) {
        notifyRefusal(refusal);

        return;
      }

      if (payload?.secret) {
        setNewSecret(payload.secret);
      }

      // The secret is live and shown exactly once, so a failed refetch must
      // not unmount its dialog or surface as an unhandled rejection; the key
      // page just shows the previous secrets until the next load.
      refetch().catch(() => undefined);
    },
  });
  const [deleteSecret, deleteSecretOpts] = useAtticApiKeySecretDeleteMutation({
    disableNestedErrorHandling: true,
    onCompleted: result => {
      const [refusal] = result.atticApiKeySecretDelete?.errors ?? [];

      setSecretToDelete(null);

      if (refusal) {
        notifyRefusal(refusal);

        return;
      }

      notify({ status: "success", text: intl.formatMessage(sectionMessages.secretDeleted) });
    },
  });
  const [revokeKey, revokeOpts] = useAtticApiKeyRevokeMutation({
    disableNestedErrorHandling: true,
    onCompleted: result => {
      const [refusal] = result.atticApiKeyRevoke?.errors ?? [];

      setRevokeOpen(false);

      if (refusal) {
        notifyRefusal(refusal);

        return;
      }

      setErrors([]);
      notify({ status: "success", text: intl.formatMessage(sectionMessages.revoked) });
    },
  });

  // Rendered in every branch below: a one-time secret must stay on screen even
  // if the refetch after rotation fails and the key is no longer in `data`.
  const secretDialog = (
    <AtticApiKeySecretDialog kind="rotated" secret={newSecret} onClose={() => setNewSecret(null)} />
  );

  if (!wellFormed) {
    return <AtticApiKeyMissing />;
  }

  if (apiKey) {
    const usage = apiKey.usage ?? [];

    return (
      <>
        <AtticApiKeyDetailsPage
          key={apiKey.id}
          apiKey={apiKey}
          usageRows={getUsageRows(usage, usageWindow)}
          usageTotals={getUsageTotals(usage)}
          permissionChoices={choices}
          exceeded={exceedsViewerPermissions(apiKey, userPermissions)}
          errors={errors}
          disabled={updateOpts.loading}
          saveButtonBarState={getMutationState(
            updateOpts.called,
            updateOpts.loading,
            errors,
            [updateOpts.error].filter(Boolean),
          )}
          addSecretState={addingSecret ? "loading" : "default"}
          now={now}
          minDay={todayLocalDay(now)}
          onSubmit={(form, expiryUnreadable) => {
            // A changed expiry is checked before anything is sent: one this
            // cannot read would otherwise go out as null, clearing the expiry
            // and reviving an expired key under a "Saved" toast.
            const expiryChanged =
              expiryUnreadable || form.expiryDay !== apiKeyToFormData(apiKey).expiryDay;
            const expiryError = expiryChanged
              ? getExpiryDayError(form.expiryDay, todayLocalDay(Date.now()), intl, expiryUnreadable)
              : null;
            // Null only for a changed expiry it cannot read, which the check
            // above has already refused: either way nothing is sent.
            const input = getUpdateInput(apiKey, form, expiryUnreadable);

            if (expiryError || !input) {
              setErrors(expiryError ? [expiryError] : []);

              return;
            }

            // Nothing changed: no request, so no empty update and no "Saved".
            if (Object.keys(input).length === 0) {
              return;
            }

            setErrors([]);
            updateKey({ variables: { id: apiKey.id, input } });
          }}
          onAddSecret={() => {
            setAddingSecret(true);
            createSecret({ variables: { apiKey: apiKey.id } });
          }}
          onDeleteSecret={setSecretToDelete}
          onRevoke={() => setRevokeOpen(true)}
        />
        {secretDialog}
        <ActionDialog
          open={secretToDelete !== null}
          variant="delete"
          title={intl.formatMessage(detailMessages.deleteSecret)}
          confirmButtonState={deleteSecretOpts.loading ? "loading" : "default"}
          onClose={() => setSecretToDelete(null)}
          onConfirm={() => secretToDelete && deleteSecret({ variables: { id: secretToDelete.id } })}
        >
          <span data-test-id="attic-api-key-delete-secret-content">
            {intl.formatMessage(
              apiKey.secrets.length === 1
                ? detailMessages.deleteLastSecretContent
                : detailMessages.deleteSecretContent,
              { lastFour: secretToDelete?.lastFour ?? "" },
            )}
          </span>
        </ActionDialog>
        <ActionDialog
          open={revokeOpen}
          variant="delete"
          title={intl.formatMessage(detailMessages.revoke)}
          confirmButtonLabel={intl.formatMessage(detailMessages.revokeConfirm)}
          confirmButtonState={revokeOpts.loading ? "loading" : "default"}
          onClose={() => setRevokeOpen(false)}
          onConfirm={() => revokeKey({ variables: { id: apiKey.id } })}
        >
          <span data-test-id="attic-api-key-revoke-content">
            {intl.formatMessage(detailMessages.revokeContent, { label: apiKey.label })}
          </span>
        </ActionDialog>
      </>
    );
  }

  if (loading) {
    return (
      <>
        <AtticApiKeyLoading />
        {secretDialog}
      </>
    );
  }

  return (
    <>
      <AtticApiKeyMissing loadFailed={!!error} />
      {secretDialog}
    </>
  );
};

export default AtticApiKeyDetailsView;
